import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { HiX } from 'react-icons/hi';
import { messageAPI, conversationAPI, profileAPI, videoCallAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Chat.css';

const Chat = () => {
  const { matchId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [showMenu, setShowMenu] = useState(null); // Track which message menu is open
  const [editingMessage, setEditingMessage] = useState(null); // Track message being edited
  const [editText, setEditText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(null); // Track delete confirmation modal
  const [showCallModal, setShowCallModal] = useState(false); // Track video call modal
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [callError, setCallError] = useState(null);
  const { userId } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [newMessage]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu !== null) {
        // Check if click is outside the menu
        const menuElement = document.querySelector('.message-menu');
        const menuButton = event.target.closest('.message-menu-btn');
        
        if (menuElement && !menuElement.contains(event.target) && !menuButton) {
          setShowMenu(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    // Load everything in parallel for better performance
    Promise.all([
      loadMessages(),
      loadConversation(),
      loadProfile()
    ]);
  }, [matchId]);

  const loadConversation = async () => {
    try {
      const response = await conversationAPI.getConversations();
      const conv = response.data.find(c => c.id === parseInt(matchId));
      if (conv) {
        setConversation(conv);
      } else {
        // The route param wasn't a valid conversation id. Don't hang on
        // "Loading conversation…"; surface a clear state instead.
        setError('This conversation could not be found.');
        setLoading(false);
      }
    } catch (err) {
      setError('Could not load this conversation. Please try again.');
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await messageAPI.getMessages(matchId);
      const messagesData = response.data.content.reverse().filter(msg => msg !== null);
      setMessages(messagesData);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load messages', err);
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Make sure we have the conversation loaded with the receiver ID
    if (!conversation || !conversation.otherUserId) {
      setError('Unable to send message. Please refresh the page.');
      return;
    }

    const messageText = newMessage.trim();
    const tempId = Date.now(); // Temporary ID for optimistic update
    
    // Optimistic update - show message immediately
    const optimisticMessage = {
      id: tempId,
      content: messageText,
      senderId: parseInt(userId),
      createdAt: new Date().toISOString(),
      read: false,
      sending: true // Flag to show it's being sent
    };
    
    setMessages([...messages, optimisticMessage]);
    setNewMessage(''); // Clear input immediately
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    setSending(true);

    try {
      setError('');
      
      // Send to server in background
      const response = await messageAPI.sendMessage(conversation.otherUserId, messageText);
      
      // Replace optimistic message with real one from server
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? { ...response.data, sending: false } : msg
      ));
      
      // PHASE 1: Credits system disabled - no need to reload
      // loadCredits() call removed for Phase 1
    } catch (err) {
      console.error('Failed to send message', err);
      
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      
      // Extract error message properly
      let errorMsg = 'Failed to send message';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.message) {
          errorMsg = err.response.data.message;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      setNewMessage(messageText); // Restore message text on error
    } finally {
      setSending(false);
    }
  };

  const handleDeleteForMe = async (messageId) => {
    try {
      await messageAPI.deleteForMe(messageId);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setShowMenu(null);
    } catch (err) {
      console.error('Failed to delete message', err);
      alert('Failed to delete message');
    }
  };

  const handleDeleteForEveryone = async (messageId) => {
    try {
      await messageAPI.deleteForEveryone(messageId);
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, content: 'This message was deleted', deletedForEveryone: true }
          : msg
      ));
      setShowMenu(null);
      setShowDeleteModal(null);
    } catch (err) {
      console.error('Failed to delete message', err);
      const errorMsg = err.response?.data?.message || err.response?.data || 'Failed to delete message';
      alert(errorMsg);
    }
  };

  const handleStartEdit = (message) => {
    setEditingMessage(message.id);
    setEditText(message.content);
    setShowMenu(null);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const handleSaveEdit = async (messageId) => {
    if (!editText.trim()) {
      alert('Message cannot be empty');
      return;
    }

    try {
      const response = await messageAPI.editMessage(messageId, editText.trim());
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? response.data : msg
      ));
      setEditingMessage(null);
      setEditText('');
    } catch (err) {
      console.error('Failed to edit message', err);
      const errorMsg = err.response?.data?.message || err.response?.data || 'Failed to edit message';
      alert(errorMsg);
    }
  };

  const canDeleteForEveryone = (message) => {
    if (message.senderId !== parseInt(userId)) return false;
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return new Date(message.createdAt) > oneHourAgo;
  };

  const canEdit = (message) => {
    if (message.senderId !== parseInt(userId)) return false;
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    return new Date(message.createdAt) > fifteenMinutesAgo;
  };

  const handleInitiateCall = async () => {
    if (!conversation || !conversation.otherUserId) {
      setCallError('Unable to start call. Please refresh the page.');
      return;
    }

    // Check if any messages have been sent in this conversation
    if (messages.length === 0) {
      setCallError('Please send a message first before starting a video call. This helps establish a connection.');
      return;
    }

    try {
      setInitiatingCall(true);
      setCallError(null);
      
      // Check if user already has an active call
      try {
        const activeCallResponse = await videoCallAPI.getActiveCall();
        if (activeCallResponse.data && activeCallResponse.data.id) {
          setCallError('You already have an active call. Please end it before starting a new one.');
          setInitiatingCall(false);
          return;
        }
      } catch (err) {
        // No active call, continue
        if (err.response?.status !== 404 && !err.response?.data?.message?.includes('No active call')) {
          console.error('Error checking active call:', err);
        }
      }
      
      const response = await videoCallAPI.initiateCall(conversation.otherUserId);
      
      // Close modal
      setShowCallModal(false);
      
      // Navigate to call screen
      navigate(`/call/${response.data.id}`);
    } catch (err) {
      console.error('Failed to initiate call:', err);
      let errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to initiate call';
      
      // Improve error message for conversation requirement
      if (errorMsg.includes('conversation') || errorMsg.includes('message first')) {
        errorMsg = 'Please send a message first before starting a video call. This helps establish a connection.';
      }
      
      setCallError(errorMsg);
    } finally {
      setInitiatingCall(false);
    }
  };

  if (loading) {
    return <div className="chat-container">Loading...</div>;
  }

  return (
    <div className="chat-container">
      <div className="chat-content">
        <div className="chat-header">
          <Link to="/inbox" className="back-button">← Back</Link>
          {conversation && (
            <>
              <div 
                className="chat-user-info"
                onClick={() => navigate(`/profile/${conversation.otherUserId}`)}
                title="Click to view profile"
              >
                {conversation.otherUserAvatar ? (
                  <img 
                    src={conversation.otherUserAvatar} 
                    alt={conversation.otherUserName}
                    className="chat-user-avatar"
                  />
                ) : (
                  <div className="chat-user-avatar-placeholder">
                    {conversation.otherUserName?.charAt(0) || '?'}
                  </div>
                )}
                <h3 className="chat-user-name">{conversation.otherUserName || 'Chat'}</h3>
              </div>
              
              {/* Free video call - WhatsApp-style: receiver accepts or hangs up */}
              <button
                className="chat-video-call-btn"
                onClick={() => setShowCallModal(true)}
                title="Start video call"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 7l-7 5 7 5V7z"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
                <span className="call-btn-label">Video Call</span>
              </button>
            </>
          )}
          {!conversation && <h3>Chat</h3>}
        </div>

        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`message ${msg.senderId === parseInt(userId) ? 'sent' : 'received'} ${msg.sending ? 'sending' : ''} ${msg.deletedForEveryone ? 'deleted' : ''}`}
                >
                  {editingMessage === msg.id ? (
                    <div className="message-edit">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="edit-textarea"
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button onClick={() => handleSaveEdit(msg.id)} className="btn-save">Save</button>
                        <button onClick={handleCancelEdit} className="btn-cancel">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Image attachment */}
                      {msg.attachmentUrl && msg.attachmentType === 'image' && (
                        <div className="message-attachment">
                          <img 
                            src={msg.attachmentUrl} 
                            alt="Attachment"
                            className="attachment-image"
                            onClick={() => window.open(msg.attachmentUrl, '_blank')}
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className="message-content">
                        {msg.content}
                        {msg.edited && <span className="edited-label"> (edited)</span>}
                      </div>
                      <div className="message-time">
                        {new Date(msg.createdAt).toLocaleTimeString()}
                        {msg.senderId === parseInt(userId) && (
                          <span className={`read-receipt ${msg.sending ? 'sending' : msg.read ? 'read' : 'unread'}`}>
                            {msg.sending ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                              </svg>
                            ) : msg.read ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"/>
                                <polyline points="20 6 9 17 4 12" transform="translate(3, 0)"/>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                      
                      {/* Message menu button - only for own messages and not deleted */}
                      {msg.senderId === parseInt(userId) && !msg.deletedForEveryone && !msg.sending && (
                        <button 
                          className="message-menu-btn"
                          onClick={() => setShowMenu(showMenu === msg.id ? null : msg.id)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="2"/>
                            <circle cx="12" cy="12" r="2"/>
                            <circle cx="12" cy="19" r="2"/>
                          </svg>
                        </button>
                      )}
                      
                      {/* Message menu dropdown */}
                      {showMenu === msg.id && (
                        <div className="message-menu">
                          {canEdit(msg) && (
                            <button onClick={() => handleStartEdit(msg)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                              Edit
                            </button>
                          )}
                          <button onClick={() => handleDeleteForMe(msg.id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                            Delete for me
                          </button>
                          {canDeleteForEveryone(msg) && (
                            <button onClick={() => setShowDeleteModal(msg.id)} className="delete-everyone">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                <line x1="10" y1="11" x2="10" y2="17"/>
                                <line x1="14" y1="11" x2="14" y2="17"/>
                              </svg>
                              Delete for everyone
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error Message Display */}
        {error && (
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            margin: '12px 16px',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ 
                fontSize: '24px', 
                lineHeight: '1',
                marginTop: '2px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  color: '#dc2626',
                  fontSize: '15px',
                  fontWeight: '600',
                  marginBottom: '4px',
                  lineHeight: '1.4'
                }}>
                  Action Required
                </div>
                <div style={{ 
                  color: '#991b1b',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  marginBottom: error.toLowerCase().includes('verify') && error.toLowerCase().includes('email') ? '12px' : '0'
                }}>
                  {error}
                </div>
                {error.toLowerCase().includes('verify') && error.toLowerCase().includes('email') && (
                  <button
                    onClick={() => navigate('/verify-email')}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
                    }}
                  >
                    Verify Email Now →
                  </button>
                )}
              </div>
              <button
                onClick={() => setError('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: '24px',
                  padding: '0',
                  lineHeight: '1',
                  opacity: '0.6',
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '1'}
                onMouseLeave={(e) => e.target.style.opacity = '0.6'}
              >
                ×
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className={`message-input ${!conversation ? 'disabled' : ''}`}>
          <input
            type="file"
            id="image-upload"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                try {
                  setSending(true);
                  // Upload image to server
                  const response = await messageAPI.uploadAttachment(file);
                  const imageUrl = response.data.url;
                  
                  // Send message with attachment
                  const messageText = newMessage || 'Image attachment';
                  const sentMessage = await messageAPI.sendMessage(
                    conversation.otherUserId,
                    messageText,
                    imageUrl,
                    'image'
                  );
                  
                  setMessages([...messages, sentMessage.data]);
                  setNewMessage('');
                  
                  // Reset file input
                  e.target.value = '';
                } catch (err) {
                  console.error('Failed to upload image:', err);
                  alert('Failed to upload image: ' + (err.response?.data?.message || err.message));
                } finally {
                  setSending(false);
                }
              }
            }}
          />
          <button
            type="button"
            className="btn-attach"
            onClick={() => document.getElementById('image-upload').click()}
            disabled={sending || !conversation}
            title="Attach image (proof of payment)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              // Send on Enter, new line on Shift+Enter
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder={!conversation ? "Loading conversation..." : "Type a message..."}
            disabled={sending || !conversation}
            rows={1}
          />
          <button type="submit" className={`btn btn-primary ${sending ? 'sending' : ''}`} disabled={sending || !conversation}>
            {sending ? (
              <>
                <span className="sending-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </>
            ) : (
              'Send'
            )}
          </button>
        </form>

        {/* PHASE 1: Out of Credits overlay removed - messaging is free */}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="delete-modal-overlay" onClick={() => setShowDeleteModal(null)}>
            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ff1493" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </div>
              <h3>Delete for Everyone?</h3>
              <p>This message will be deleted for you and the recipient. This action cannot be undone.</p>
              <div className="delete-modal-actions">
                <button 
                  className="btn-delete-confirm" 
                  onClick={() => handleDeleteForEveryone(showDeleteModal)}
                >
                  Delete
                </button>
                <button 
                  className="btn-delete-cancel" 
                  onClick={() => setShowDeleteModal(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video Call Modal */}
        {showCallModal && conversation && (
          <div className="modal-overlay" onClick={() => setShowCallModal(false)}>
            <div className="modal-content call-modal-chat" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowCallModal(false)} aria-label="Close"><HiX /></button>
              
              <div className="call-modal-simple">
                <div className="call-profile-section">
                  {conversation.otherUserAvatar ? (
                    <img 
                      src={conversation.otherUserAvatar} 
                      alt={conversation.otherUserName}
                      className="call-profile-pic"
                    />
                  ) : (
                    <div className="call-profile-pic-placeholder">
                      {conversation.otherUserName?.charAt(0) || '?'}
                    </div>
                  )}
                  <h2>Call {conversation.otherUserName}?</h2>
                  <p className="call-subtitle">Start a video call</p>
                </div>

                {callError && (
                  <div className="call-error-simple">
                    <p>{callError}</p>
                  </div>
                )}

                <div className="call-info-note">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <div>
                    <p>
                      {conversation.otherUserName} will receive a notification and has 30 seconds to answer.
                    </p>
                  </div>
                </div>

                <div className="call-actions">
                  <button 
                    className="btn-call-now"
                    onClick={handleInitiateCall}
                    disabled={initiatingCall}
                  >
                    {initiatingCall ? 'Calling...' : 'Start Call'}
                  </button>
                  <button 
                    className="btn-cancel"
                    onClick={() => setShowCallModal(false)}
                    disabled={initiatingCall}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
